/**
 * Finance Services
 * 
 * This module provides services for finance-related functionality including:
 * - Fund Request Beta Testers management
 * - Campaign search and management
 * - Vendor import/clear operations  
 * - MIP sync operations
 * - Stripe payouts
 */
import { apiUrl, authHeaders } from './auth';

export interface Campaign {
    id: number;
    name: string;
    patient_name?: string;
    status?: string;
}

export interface BetaTester {
    id: number;
    campaign: {
        id: number;
        name: string;
        patient_name?: string;
    };
    notes: string;
    added_by?: string;
    created_date: string;
    is_active: boolean;
    vendor_count: number;
}

export interface AddBetaTesterRequest {
    campaign_id: number;
    notes?: string;
}

export interface SkyApiSettings {
    redirect_uri: string | null;
    suggested_redirect_uri: string;
    effective_redirect_uri: string;
    scopes: string;
    has_client_secret: boolean;
    has_client_id: boolean;
    is_configured: boolean;
    is_connected: boolean;
    has_refresh_token: boolean;
    token_expires_at: string | null;
    last_error: string | null;
}

/**
 * Fetch all Fund Request Beta Testers
 */
export const fetchFundRequestBetaTesters = async (): Promise<BetaTester[]> => {
    const response = await fetch(`${apiUrl}/v2/fund-request-beta-testers`, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch failed:', response.status, errorText);
        throw new Error('Failed to fetch beta testers');
    }

    const text = await response.text();
    try {
        const data = text ? JSON.parse(text) : [];
        return Array.isArray(data) ? data : [];
    } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        console.error('Response text:', text);
        throw new Error('Invalid response format from server');
    }
};

/**
 * Search campaigns by ID, name, patient name, or NTAF ID
 * 
 * Note: The backend automatically detects if the query is numeric and treats it as an NTAF ID search.
 * Numeric searches will automatically filter to only campaigns with NTAF IDs.
 */
export const searchCampaigns = async (query: string): Promise<Campaign[]> => {
    if (!query.trim()) {
        return [];
    }

    const response = await fetch(`${apiUrl}/admin/quickSearchCampaigns?search=${encodeURIComponent(query)}`, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Search failed:', response.status, errorText);
        throw new Error('Failed to search campaigns');
    }

    const text = await response.text();
    try {
        const data = text ? JSON.parse(text) : [];
        // The quickSearchCampaigns endpoint returns a paginated response with results array
        const results = data.results || data;
        return Array.isArray(results) ? results : [];
    } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        console.error('Response text:', text);
        throw new Error('Invalid response format from server');
    }
};

/**
 * Add campaigns as Fund Request Beta Testers
 */
export const addFundRequestBetaTesters = async (requests: AddBetaTesterRequest[]): Promise<number> => {
    const promises = requests.map(request =>
        fetch(`${apiUrl}/v2/fund-request-beta-testers`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(request),
        })
    );

    const responses = await Promise.all(promises);
    const successCount = responses.filter(r => r.ok).length;

    if (successCount === 0) {
        throw new Error('Failed to add any beta testers');
    }

    return successCount;
};

/**
 * Remove a Fund Request Beta Tester
 */
export const removeFundRequestBetaTester = async (id: number): Promise<void> => {
    const response = await fetch(`${apiUrl}/v2/fund-request-beta-testers/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete failed:', response.status, errorText);
        throw new Error('Failed to remove beta tester');
    }
};

/**
 * Import vendors for a campaign
 */
export const importVendorsForCampaign = async (campaignId: number): Promise<{ message: string; count: number }> => {
    const response = await fetch(`${apiUrl}/admin/campaigns/${campaignId}/import-vendors`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Import vendors failed:', response.status, errorText);
        throw new Error('Failed to import vendors');
    }

    const data = await response.json();
    return data;
};

/**
 * Fetch current SKY API OAuth configuration details.
 */
export const fetchSkyApiSettings = async (): Promise<SkyApiSettings> => {
    const response = await fetch(`${apiUrl}/v2/sky-api/settings`, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch SKY settings failed:', response.status, errorText);
        throw new Error('Failed to load SKY API settings');
    }

    return response.json();
};

/**
 * Request an authorization URL for the SKY API OAuth flow.
 */
export const startSkyApiAuthorization = async (): Promise<string> => {
    const response = await fetch(`${apiUrl}/v2/sky-api/authorize`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Create SKY authorization URL failed:', response.status, errorText);
        try {
            const parsed = JSON.parse(errorText);
            throw new Error(parsed?.error || 'Failed to start SKY API authorization');
        } catch (err) {
            throw new Error('Failed to start SKY API authorization');
        }
    }

    const data = await response.json();
    return data.authorization_url as string;
};

/**
 * Disconnect and clear stored SKY API tokens.
 */
export const disconnectSkyApi = async (): Promise<SkyApiSettings> => {
    const response = await fetch(`${apiUrl}/v2/sky-api/disconnect`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Disconnect SKY API failed:', response.status, errorText);
        throw new Error('Failed to disconnect SKY API');
    }

    return response.json();
};

/**
 * Clear all vendors and fund requests for a campaign
 */
export const clearVendorsForCampaign = async (campaignId: number): Promise<{ message: string; vendor_count: number; fund_request_count: number }> => {
    const response = await fetch(`${apiUrl}/admin/campaigns/${campaignId}/clear-vendors`, {
        method: 'DELETE',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Clear vendors failed:', response.status, errorText);
        throw new Error('Failed to clear vendors');
    }

    const data = await response.json();
    return data;
};

/**
 * Trigger MIP sync with specified date range
 */
export const triggerMipSync = async (startDate: string, endDate: string): Promise<{ 
    message: string; 
    start_date: string; 
    end_date: string; 
    status: string;
    stats?: string;
    error?: string;
    success: boolean;
}> => {
    const response = await fetch(`${apiUrl}/v2/mip-sync`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
            start_date: startDate,
            end_date: endDate,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('MIP sync trigger failed:', response.status, errorText);
        let message = 'Failed to trigger MIP sync';
        try {
            message = JSON.parse(errorText).error || message;
        } catch {
            // Not JSON; keep the generic message.
        }
        throw new Error(message);
    }

    const data = await response.json();
    return data;
};

/**
 * Test SKY API custom field categories endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiCustomFields = async (useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-custom-fields`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiCustomFields service call ===');
    console.log('URL:', url);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API custom fields');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API custom field category by ID endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiCategoryById = async (categoryId: number, useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-category-by-id`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiCategoryById service call ===');
    console.log('URL:', url);
    console.log('Category ID:', categoryId);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ category_id: categoryId, use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API category by ID');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API fund categories endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiFundCategories = async (useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-fund-categories`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiFundCategories service call ===');
    console.log('URL:', url);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API fund categories');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API category values endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiCategoryValues = async (categoryName: string, useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-category-values`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiCategoryValues service call ===');
    console.log('URL:', url);
    console.log('Category Name:', categoryName);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ category_name: categoryName, use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API category values');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API fund categories list endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiFundCategoriesList = async (useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-fund-categories-list`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiFundCategoriesList service call ===');
    console.log('URL:', url);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API fund categories list');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API recent funds endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiRecentFunds = async (useCache: boolean = true, limit: number = 3, days: number = 365): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
    url?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-recent-funds`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiRecentFunds service call ===');
    console.log('URL:', url);
    console.log('Use cache:', useCache);
    console.log('Limit:', limit);
    console.log('Days:', days);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ use_cache: useCache, limit, days }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API recent funds');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API fund by ID endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiFundById = async (fundId: string, useCache: boolean = true): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
    url?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-fund-by-id`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiFundById service call ===');
    console.log('URL:', url);
    console.log('Fund ID:', fundId);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ fund_id: fundId, use_cache: useCache }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API fund by ID');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Fetch fund details from SKY API by fund ID.
 */
export const fetchSkyApiFundDetails = async (fundId: string): Promise<{
    success: boolean;
    status_code: number | null;
    data: any;
    error: string | null;
}> => {
    const url = `${apiUrl}/v2/sky-api/funds/${fundId}`;
    const headers = authHeaders();
    
    console.log('=== fetchSkyApiFundDetails service call ===');
    console.log('URL:', url);
    console.log('Fund ID:', fundId);
    
    const response = await fetch(url, {
        method: 'GET',
        headers: headers,
    });

    console.log('Response status:', response.status);
    
    const data = await response.json();
    console.log('Parsed response data:', data);
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to fetch fund details from SKY API');
    }

    return data;
};

/**
 * Update fund details in SKY API.
 */
export const updateSkyApiFund = async (fundId: string, updateData: {
    fund_category_id?: number | null;
    description?: string;
    start_date?: string;
    goal?: number | null;
    inactive?: boolean;
    fund_type_id?: number | null;
}): Promise<{
    success: boolean;
    status_code: number | null;
    data: any;
    error: string | null;
}> => {
    const url = `${apiUrl}/v2/sky-api/funds/${fundId}/update`;
    const headers = authHeaders();
    
    console.log('=== updateSkyApiFund service call ===');
    console.log('URL:', url);
    console.log('Fund ID:', fundId);
    console.log('Update data:', updateData);
    
    const response = await fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(updateData),
    });

    console.log('Response status:', response.status);
    
    const data = await response.json();
    console.log('Parsed response data:', data);
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to update fund in SKY API');
    }

    return data;
};

/**
 * Fetch fund custom fields from SKY API by fund ID.
 */
export const fetchSkyApiFundCustomFields = async (fundId: string): Promise<{
    success: boolean;
    status_code: number | null;
    data: any;
    error: string | null;
}> => {
    const url = `${apiUrl}/v2/sky-api/funds/${fundId}/customfields`;
    const headers = authHeaders();
    
    console.log('=== fetchSkyApiFundCustomFields service call ===');
    console.log('URL:', url);
    console.log('Fund ID:', fundId);
    
    const response = await fetch(url, {
        method: 'GET',
        headers: headers,
    });

    console.log('Response status:', response.status);
    
    const data = await response.json();
    console.log('Parsed response data:', data);
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to fetch fund custom fields from SKY API');
    }

    return data;
};

/**
 * Test SKY API code tables endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiCodeTables = async (
    name?: string,
    includeHiddenSystem?: boolean,
    limit?: number,
    offset?: number,
    useCache: boolean = true
): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
    url?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-code-tables`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiCodeTables service call ===');
    console.log('URL:', url);
    console.log('Name:', name);
    console.log('Include Hidden System:', includeHiddenSystem);
    console.log('Limit:', limit);
    console.log('Offset:', offset);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
            name, 
            include_hidden_system: includeHiddenSystem,
            limit,
            offset,
            use_cache: useCache 
        }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API code tables');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Test SKY API code table entries endpoint.
 * This will automatically refresh the access token if expired.
 */
export const testSkyApiCodeTableEntries = async (
    codeTableId: string,
    longDescription?: string,
    shortDescription?: string,
    numericValue?: number,
    includeInactive?: boolean,
    limit?: number,
    offset?: number,
    useCache: boolean = true
): Promise<{
    success: boolean;
    status_code: number | null;
    data: unknown;
    error: string | null;
    cached?: boolean;
    cached_at?: string;
    url?: string;
}> => {
    const url = `${apiUrl}/v2/sky-api/test-code-table-entries`;
    const headers = authHeaders();
    
    console.log('=== testSkyApiCodeTableEntries service call ===');
    console.log('URL:', url);
    console.log('Code Table ID:', codeTableId);
    console.log('Long Description:', longDescription);
    console.log('Short Description:', shortDescription);
    console.log('Numeric Value:', numericValue);
    console.log('Include Inactive:', includeInactive);
    console.log('Limit:', limit);
    console.log('Offset:', offset);
    console.log('Use cache:', useCache);
    console.log('Headers:', headers);
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
            code_table_id: codeTableId,
            long_description: longDescription,
            short_description: shortDescription,
            numeric_value: numericValue,
            include_inactive: includeInactive,
            limit,
            offset,
            use_cache: useCache 
        }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    let data;
    try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
    
    if (!response.ok && !data.error) {
        console.error('Request failed with status', response.status);
        throw new Error('Failed to test SKY API code table entries');
    }

    console.log('=== Returning data ===');
    return data;
};

/**
 * Stripe Payout interface matching the API response
 */
export interface StripePayout {
    id: string;
    amount: number;
    currency: string;
    arrival_date: string | null;
    created: string | null;
    status: 'pending' | 'paid' | 'failed' | 'canceled' | 'in_transit';
    source_type: string;
    description: string | null;
    method: string;
    automatic: boolean;  // True if scheduled payout, false if manually triggered
    has_sync_record: boolean;  // True if payout has been processed through NXT sync
    // MIP sync status fields
    mip_sync_status: 'pending' | 'processing' | 'synced' | 'failed' | 'n/a' | 'not_processed';
    mip_synced_at: string | null;
    mip_records_created: number;
    mip_error_message?: string;
    mip_failed_documents?: Array<{
        doc_number: number;
        document_id: string;
        error: string;
    }>;
    mip_jv_session_id?: string;
    // NXT sync status (when sync record exists)
    nxt_sync_status?: 'pending' | 'processing' | 'completed' | 'failed';
    charges_total?: number;
    charges_processed?: number;
    charges_failed?: number;
}

export interface StripePayoutsResponse {
    payouts: StripePayout[];
    has_more: boolean;
    next_cursor: string | null;
}

/**
 * Fetch Stripe payouts with optional pagination and filtering
 */
export const fetchStripePayouts = async (params?: {
    limit?: number;
    starting_after?: string;
    status?: string;
}): Promise<StripePayoutsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.starting_after) queryParams.append('starting_after', params.starting_after);
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/v2/stripe/payouts${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch Stripe payouts failed:', response.status, errorText);
        throw new Error('Failed to fetch Stripe payouts');
    }

    const data = await response.json();
    return data;
};

/**
 * Extended payout interface for synced records (includes test payouts)
 */
export interface SyncedStripePayout extends StripePayout {
    is_test_payout: boolean;
    error_message?: string;
}

export interface SyncedStripePayoutsResponse {
    payouts: SyncedStripePayout[];
    has_more: boolean;
    next_cursor: string | null;
    total_count: number;
}

/**
 * Fetch synced payouts from local database (includes test payouts)
 *
 * This endpoint returns payouts that have been processed through NXT sync,
 * including test payouts created via test_nxt_sync command.
 */
export const fetchSyncedPayouts = async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
}): Promise<SyncedStripePayoutsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/v2/stripe/payouts/synced${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch synced payouts failed:', response.status, errorText);
        throw new Error('Failed to fetch synced payouts');
    }

    const data = await response.json();
    return data;
};

/**
 * MIP Transform Response interface
 */
export interface MipTransformResponse {
    success: boolean;
    payout_id: string;
    mip_sync_status: string;
    mip_synced_at: string | null;
    records: Array<{
        date: string;
        batch_id: string;
        doc_number: number;
        fund_id: string;
        gift_code: string;
        transaction_type: string;
        gl_code: string;
        cost_center: string;
        program_code: string;
        fas117: string;
        region: string;
        department: string;
        debit_amount: string;
        credit_amount: string;
        charge_id: string;
    }>;
    charges_processed: number;
    charges_failed: number;
    charges_skipped: number;
    total_records: number;
    errors: string[];
    preview?: boolean;
    mip_error_message?: string;
    mip_failed_documents?: Array<{
        doc_number: number;
        document_id: string;
        error: string;
    }>;
    mip_jv_session_id?: string;
    jv_sync?: {
        success: boolean;
        session_id: string;
        documents_created: number;
        documents_failed: number;
        cleanup_performed: boolean;
        cleanup_errors: string[];
        failed_documents: Array<{
            doc_number: number;
            document_id: string;
            error: string;
        }>;
        created_documents: Array<{
            doc_number: number;
            document_id: string;
        }>;
        errors: string[];
    };
}

const MIP_POST_POLL_INTERVAL_MS = 3000;
// A post takes about a second per journal document, so this covers even a
// very large payout. The post keeps running on the server past it regardless.
const MIP_POST_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Post a payout to MIP and wait for the outcome.
 *
 * A large payout takes longer to post than a request may stay open, so the
 * server posts it in the background: this starts the post, then polls the
 * payout's MIP status until it is no longer "processing" and returns that.
 */
export const triggerMipGiftSync = async (payoutId: string): Promise<MipSyncStatusResponse> => {
    const url = `${apiUrl}/v2/stripe/payouts/${payoutId}/mip-transform`;

    const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start the MIP sync');
    }

    const deadline = Date.now() + MIP_POST_POLL_TIMEOUT_MS;
    let outcome = await getMipSyncStatus(payoutId);
    while (outcome.mip_sync_status === 'processing') {
        if (Date.now() > deadline) {
            throw new Error('The MIP sync is still running. Check back in a few minutes.');
        }
        await new Promise((resolve) => setTimeout(resolve, MIP_POST_POLL_INTERVAL_MS));
        outcome = await getMipSyncStatus(payoutId);
    }

    return outcome;
};

/**
 * Preview MIP gift sync transformation for a payout (without saving)
 */
export const previewMipGiftSync = async (payoutId: string): Promise<MipTransformResponse> => {
    const url = `${apiUrl}/v2/stripe/payouts/${payoutId}/mip-preview`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to preview MIP transformation');
    }

    return response.json();
};

/**
 * Process a payout to create sync records (for payouts not yet processed)
 */
export const processPayoutSync = async (
    payoutId: string,
    options?: { reset_skipped?: boolean }
): Promise<{
    payout_id: string;
    success: boolean;
    processed: number;
    failed: number;
    skipped: number;
    errors: Array<{ charge_id?: string; error?: string }>;
    charges_reset?: number;
}> => {
    const url = `${apiUrl}/v2/stripe/payouts/sync`;

    const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ 
            payout_id: payoutId,
            reset_skipped: options?.reset_skipped || false,
        }),
    });

    const data = await response.json();
    
    if (!response.ok) {
        throw new Error(data.error || 'Failed to process payout');
    }

    return data;
};

export interface MipSyncStatusResponse {
    payout_id: string;
    mip_sync_status: string;
    mip_synced_at: string | null;
    mip_records_created: number;
    mip_error_message: string;
    mip_failed_documents: Array<{
        doc_number: number;
        document_id: string;
        error: string;
    }>;
    mip_jv_session_id: string;
}

/**
 * Get MIP sync status for a payout
 */
export const getMipSyncStatus = async (payoutId: string): Promise<MipSyncStatusResponse> => {
    const url = `${apiUrl}/v2/stripe/payouts/${payoutId}/mip-status`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get MIP sync status');
    }

    return response.json();
};

/**
 * Balance Transaction interface for payout details
 */
export interface StripeBalanceTransaction {
    id: string;
    amount: number;
    fee: number;
    net: number;
    currency: string;
    type: string;  // charge, refund, payout, etc.
    description: string | null;
    source: string | null;  // The charge/refund/etc ID
    created: string | null;
    available_on: string | null;
    status: string;
    reporting_category: string;
    // Manual RE + MIP completion tracking, present on refund rows that map to
    // a DonationRefund record (HHLTB-2586).
    refund_id?: number;
    completion_status?: 'pending' | 'complete';
    completed_at?: string | null;
    completed_by_id?: number | null;
}

export interface ChargeMetadataResponse {
    charge_id: string;
    metadata: Record<string, string> | null;
}

export interface PayoutDetailResponse {
    payout: StripePayout & { is_test_payout?: boolean };
    transactions: StripeBalanceTransaction[];
    has_more: boolean;
    next_cursor: string | null;
    totals: {
        charges: number;
        refunds: number;
        fees: number;
        net: number;
        count: number;
    };
    manual_payout_notice?: string;  // Present when payout is manual (transactions unavailable)
    auto_debit_notice?: string;  // Present when payout is an auto-debit (transactions unavailable)
    is_local_record?: boolean;  // Present when viewing a test payout from local records
    local_record_notice?: string;  // Notice explaining this is a test payout
    mip_always_delete?: boolean;  // True when MIP_JV_ALWAYS_DELETE is enabled server-side
}

/**
 * Fetch payout details with balance transactions
 */
export const fetchPayoutDetail = async (
    payoutId: string,
    params?: {
        limit?: number;
        starting_after?: string;
    }
): Promise<PayoutDetailResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.starting_after) queryParams.append('starting_after', params.starting_after);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/v2/stripe/payouts/${payoutId}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch payout details');
    }

    return response.json();
};

/**
 * Fetch metadata for a specific Stripe charge
 */
export const fetchChargeMetadata = async (
    chargeId: string
): Promise<ChargeMetadataResponse> => {
    const url = `${apiUrl}/v2/stripe/charges/${chargeId}/metadata`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch charge metadata');
    }

    return response.json();
};

/**
 * Charge sync record interface for NXT gift tracking
 */
export interface ChargeSyncRecord {
    charge_id: string;
    status: 'pending' | 'synced' | 'failed' | 'skipped';
    amount: number;
    tip_amount: number;
    donation_amount: number;
    campaign_id: number | null;
    campaign_name: string;
    patient_name: string;
    ntaf_id: string;
    is_restricted: boolean;
    fund_id: string;
    fee_amount: number;
    donor_name: string;
    donor_email: string;
    is_anonymous: boolean;
    show_amount: boolean;
    gift_code: string;
    nxt_gift_id: string;
    nxt_tip_gift_id: string;
    primary_loc_id: number | null;
    tip_loc_id: number | null;
    payment_method: string;
    card_brand: string;
    error_message: string;
    updated_at: string | null;
}

export interface PayoutChargeSyncsResponse {
    payout_id: string;
    charges: ChargeSyncRecord[];
    summary: {
        total: number;
        synced: number;
        pending: number;
        failed: number;
        skipped: number;
    };
}

/**
 * Fetch charge sync records for a payout (NXT gifts)
 */
export const fetchPayoutChargeSyncs = async (
    payoutId: string
): Promise<PayoutChargeSyncsResponse> => {
    const url = `${apiUrl}/v2/stripe/payouts/${payoutId}/charges`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch charge sync records');
    }

    return response.json();
};

/**
 * Reprocess a charge to NXT - deletes existing gift(s) and recreates with updated logic
 */
export interface ReprocessChargeResponse {
    success: boolean;
    charge_id: string;
    deleted_gifts: string[];
    created_gifts: string[];
    error: string | null;
}

export const reprocessCharge = async (
    chargeId: string,
    force: boolean = false
): Promise<ReprocessChargeResponse> => {
    const url = `${apiUrl}/v2/stripe/charges/${chargeId}/reprocess${force ? '?force=true' : ''}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
    });

    return response.json();
};

/**
 * Preview MIP transformation for a single charge
 */
export interface ChargeMipPreviewResponse {
    success: boolean;
    charge_id: string;
    records: MipTransformResponse['records'];
    total_records: number;
    errors: string[];
}

export const previewChargeMipTransform = async (
    chargeId: string
): Promise<ChargeMipPreviewResponse> => {
    const url = `${apiUrl}/v2/stripe/charges/${chargeId}/mip-preview`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to preview MIP transformation for charge');
    }

    return response.json();
};

// ──────────────────────────────────────────────────────────────────────
// Mock Payout Creation
// ──────────────────────────────────────────────────────────────────────

export interface MockPayoutRequest {
    payment_methods: string[];
    amount?: number;
    tip?: number | null;
    no_tip?: boolean;
    donor_email?: string;
    donor_name?: string;
    ntaf?: string;
    campaign_id?: number | null;
    failure_scenarios?: Record<string, string | null>;
    dry_run?: boolean;
}

export interface MockPayoutChargeResult {
    charge_id: string;
    payment_method: string;
    label: string;
    amount: number;
    tip: number;
    fee: number;
    net: number;
    campaign_id: number;
    campaign_name: string;
    status: 'succeeded' | 'failed';
    failure_scenario: string | null;
    failure_code?: string;
    failure_message?: string;
    error: string | null;
}

export interface MockPayoutResponse {
    success: boolean;
    dry_run?: boolean;
    payout_id: string;
    charges: MockPayoutChargeResult[];
    summary: {
        total: number;
        processed: number;
        failed: number;
        total_amount: number;
        total_tips: number;
        total_fees: number;
        total_net: number;
    };
}

/** All available payment method keys and their labels */
export const PAYMENT_METHOD_OPTIONS = [
    { key: 'visa', label: 'Visa', type: 'card' },
    { key: 'mastercard', label: 'Mastercard', type: 'card' },
    { key: 'amex', label: 'American Express', type: 'card' },
    { key: 'discover', label: 'Discover', type: 'card' },
    { key: 'ach', label: 'ACH Direct Debit', type: 'ach' },
    { key: 'apple_pay', label: 'Apple Pay (Visa)', type: 'card' },
    { key: 'google_pay', label: 'Google Pay (Mastercard)', type: 'card' },
] as const;

/** Card failure scenarios */
export const CARD_FAILURE_OPTIONS = [
    { key: 'card_declined', label: 'Card Declined', description: 'Generic decline' },
    { key: 'insufficient_funds', label: 'Insufficient Funds', description: 'Not enough balance' },
    { key: 'expired_card', label: 'Expired Card', description: 'Card past expiration' },
    { key: 'incorrect_cvc', label: 'Incorrect CVC', description: 'Wrong security code' },
    { key: 'lost_card', label: 'Lost Card', description: 'Card reported lost' },
    { key: 'stolen_card', label: 'Stolen Card', description: 'Card reported stolen' },
    { key: 'fraud_suspected', label: 'Fraud (Radar Block)', description: 'Blocked by Stripe Radar' },
    { key: 'processing_error', label: 'Processing Error', description: 'Temporary processing failure' },
] as const;

/** ACH failure scenarios */
export const ACH_FAILURE_OPTIONS = [
    { key: 'insufficient_funds', label: 'Insufficient Funds', description: 'ACH R01' },
    { key: 'account_closed', label: 'Account Closed', description: 'ACH R02' },
    { key: 'no_account', label: 'No Account', description: 'ACH R03' },
    { key: 'invalid_account_number', label: 'Invalid Account Number', description: 'ACH R04' },
    { key: 'authorization_revoked', label: 'Authorization Revoked', description: 'ACH R07' },
    { key: 'payment_stopped', label: 'Payment Stopped', description: 'ACH R08' },
    { key: 'customer_not_authorized', label: 'Customer Not Authorized', description: 'ACH R10' },
] as const;

/**
 * Create a mock payout with configurable payment methods and failure scenarios
 */
export const createMockPayout = async (
    request: MockPayoutRequest
): Promise<MockPayoutResponse> => {
    const url = `${apiUrl}/v2/stripe/payouts/mock`;

    const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to create mock payout');
    }

    return data;
};
